import { html, TemplateResult, CSSResultGroup } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { OutlineElement } from '../../base/outline-element/outline-element';
import { SlotsNGController as SlotsController } from '../../controllers/slots-ng-controller';
import { screenSizeMap } from '../../controllers/screen-size-controller';
import { ResizeController } from '../../controllers/resize-controller';
import '../sic-container/sic-container';
import { backgroundColor } from '../sic-container/sic-container';

import componentStyles from './sic-accordion-block.css.lit';

/**
 * The SIC Accordion Block component.
 * @element sic-accordion-block
 * @slot block-heading
 * @slot body
 * @slot cta
 * @slot image
 * @slot accordion
 *
 * @prop background-color
 */

@customElement('sic-accordion-block')
export class SicAccordionBlock extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];

  slots = new SlotsController(this);
  resizeController: ResizeController;

  // The resizeController is initialized here because visibleBreakpoint is not set yet when constructor runs
  connectedCallback() {
    super.connectedCallback();
    this.resizeController = new ResizeController(this, {
      breakpoints: [screenSizeMap.get(this.visibleBreakpoint) as number],
    });
  }

  @property({
    type: String,
    reflect: true,
    attribute: 'background-color',
  })
  backgroundColor: backgroundColor = 'none';

  /**
   * The breakpoint at which the switch from mobile to desktop layout.
   *
   * This is done as a variable to ensure flexibility for future usages.
   */
  @property({ type: String, attribute: 'breakpoint' })
  visibleBreakpoint = 'md';

  @state() private isStacked = false;

  render(): TemplateResult {
    this.isStacked = this.resizeController.currentBreakpointRange === 0;
    const wrapperClasses = {
      'layout-container': true,
      'layout-stacked': this.isStacked,
    };

    return html`
      <sic-container
        background-color="${this.backgroundColor}"
        container-width="full"
        bottom-padding="none"
        top-padding="none"
        class="sic-accordion-block--inner"
      >
        ${this.slots.conditionalSlot('image', false)}
        <div class="${classMap(wrapperClasses)}">
          ${this.contentTemplate()} ${this.accordionTemplate()}
        </div>
      </sic-container>
    `;
  }

  contentTemplate(): TemplateResult {
    return html` <div class="content-layout">
      <div class="content">
        ${this.headingTemplate()} ${this.slots.conditionalSlot('body', false)}
        ${this.slots.conditionalSlot('cta', false)}
      </div>
    </div>`;
  }
  headingTemplate(): TemplateResult {
    if (this.slots.slotExists('block-heading')) {
      return html` <sic-heading level-size="5" class="content-heading">
        ${this.slots.conditionalSlot('block-heading', true)}
      </sic-heading>`;
    }
    return html``;
  }
  accordionTemplate(): TemplateResult {
    return html`
      <div class="media-layout">
        <div class="accordion-container">
          ${this.slots.conditionalSlot('accordion', false)}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sic-accordion-block': SicAccordionBlock;
  }
}
